// Builds and prints a standalone one-page PDF-ready document for an SDLT estimate.
// Uses a hidden iframe + window.print() so there is zero runtime dependency, and
// the user keeps control (Save as PDF / print preview native dialog).
export const printSdltEstimate = ({
  price,
  total,
  statusText,
  bands,
  additional,
  firstTimeBuyer,
}) => {
  const fmt = (n) => `£${Math.round(n).toLocaleString("en-GB")}`;
  const dateStr = new Date().toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const rowsHtml = bands
    .map(
      (b) => `
      <tr>
        <td class="band">${b.label}</td>
        <td class="rate">${b.rate_label}</td>
        <td class="tax">${fmt(b.tax)}</td>
      </tr>`
    )
    .join("");

  const chips = [
    additional ? "Additional property — 5% surcharge applied" : null,
    firstTimeBuyer ? "First‑time buyer relief applied" : null,
  ]
    .filter(Boolean)
    .map((label) => `<span class="chip">${label}</span>`)
    .join("");

  const html = `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>SDLT Estimate — ${fmt(price)} purchase</title>
    <style>
      @page { size: A4; margin: 18mm 16mm; }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; background: #F5F0E6; color: #1C2620; font-family: 'Manrope','Helvetica Neue',Arial,sans-serif; }
      .page { max-width: 720px; margin: 0 auto; padding: 32px 36px; background: #ffffff; border: 1px solid #e4d9bd; }
      header { border-bottom: 1px solid #e4d9bd; padding-bottom: 18px; margin-bottom: 24px; display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; }
      .brand { font-family: 'Cormorant Garamond','Georgia',serif; font-size: 22px; letter-spacing: 0.1em; color: #0F3226; }
      .brand small { display:block; letter-spacing: 0.28em; font-size: 10px; color: #8a7a4f; margin-top: 4px; text-transform: uppercase; font-family: 'Manrope',sans-serif; font-weight: 700; }
      .date { font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase; color: #8a8f86; font-weight: 700; white-space: nowrap; }
      h1 { font-family: 'Cormorant Garamond','Georgia',serif; font-weight: 500; font-size: 34px; margin: 0 0 6px; color: #0F3226; line-height: 1.1; }
      h1 em { font-style: italic; color: #a67a1d; font-weight: 500; }
      .lede { font-size: 13px; line-height: 1.7; color: #4D6359; margin: 0 0 26px; max-width: 540px; }
      .summary { background: #faf4e3; border: 1px solid #e4d9bd; padding: 20px 22px; display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 12px; margin-bottom: 10px; }
      .summary .eyebrow { font-size: 10px; letter-spacing: 0.24em; text-transform: uppercase; color: #8a7a4f; font-weight: 700; margin: 0 0 6px; }
      .summary .price { font-family: 'Cormorant Garamond','Georgia',serif; font-size: 22px; color: #0F3226; }
      .summary .total { text-align: right; }
      .summary .total .amount { font-family: 'Cormorant Garamond','Georgia',serif; font-size: 38px; color: #a67a1d; line-height: 1; }
      .chips { margin-top: 14px; display: flex; flex-wrap: wrap; gap: 8px; }
      .chip { display: inline-block; font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase; font-weight: 700; color: #8a6a1f; background: #faf0d3; border: 1px solid #e4d9bd; padding: 6px 10px; }
      .status { font-size: 12.5px; color: #4D6359; margin: 14px 0 22px; }
      table { width: 100%; border-collapse: collapse; font-size: 13px; }
      thead th { text-align: left; font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; color: #8a7a4f; font-weight: 700; border-bottom: 1px solid #e4d9bd; padding: 10px 12px; background: #faf4e3; }
      thead th.rate, thead th.tax { text-align: right; }
      tbody td { padding: 12px; border-bottom: 1px solid #efe6cd; }
      tbody td.band { color: #1C2620; }
      tbody td.rate { text-align: right; color: #4D6359; }
      tbody td.tax { text-align: right; font-weight: 600; color: #1C2620; }
      tfoot td { padding: 14px 12px; font-weight: 700; }
      tfoot td.total-label { color: #0F3226; }
      tfoot td.total-amount { text-align: right; font-family: 'Cormorant Garamond','Georgia',serif; font-size: 20px; color: #a67a1d; }
      .fine { margin-top: 26px; padding-top: 16px; border-top: 1px solid #e4d9bd; font-size: 11px; line-height: 1.7; color: #8a8f86; }
      .cta { margin-top: 10px; font-size: 12px; color: #0F3226; }
      .cta a { color: #a67a1d; text-decoration: none; }
      @media print {
        html, body { background: #ffffff; }
        .page { border: none; padding: 0; max-width: none; }
      }
    </style>
  </head>
  <body>
    <div class="page">
      <header>
        <div class="brand">ELIN&rsquo;S CONSULTANCY<small>Stamp Duty Estimate</small></div>
        <div class="date">${dateStr}</div>
      </header>

      <h1>Your Stamp Duty, <em>Calculated Clearly</em></h1>
      <p class="lede">A tidy one‑page breakdown of your Stamp Duty Land Tax estimate, ready to share with your solicitor. Based on residential SDLT rates for England from 1 April 2025.</p>

      <div class="summary">
        <div>
          <p class="eyebrow">Purchase price</p>
          <p class="price">${fmt(price)}</p>
        </div>
        <div class="total">
          <p class="eyebrow">Total SDLT payable</p>
          <p class="amount">${fmt(total)}</p>
        </div>
      </div>
      ${chips ? `<div class="chips">${chips}</div>` : ""}
      <p class="status">${statusText}</p>

      <table>
        <thead>
          <tr>
            <th class="band">Band</th>
            <th class="rate">Rate</th>
            <th class="tax">Tax Due</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot>
          <tr>
            <td class="total-label" colspan="2">Total</td>
            <td class="total-amount">${fmt(total)}</td>
          </tr>
        </tfoot>
      </table>

      <p class="fine">This estimate is a guide, not tax advice. Non‑UK residents pay a further 2% on top. Please speak to your solicitor and reconfirm figures at exchange — rates and reliefs can change.</p>
      <p class="cta">Prepared by Elin&rsquo;s Consultancy &nbsp;·&nbsp; <a href="https://elinsconsultancy.co.uk/contact">elinsconsultancy.co.uk/contact</a></p>
    </div>
  </body>
</html>`;

  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();

  const trigger = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch (e) {
      /* no-op */
    }
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 1500);
  };

  // Give the iframe a tick to paint before invoking print.
  if (iframe.contentWindow && iframe.contentDocument?.readyState === "complete") {
    setTimeout(trigger, 150);
  } else {
    iframe.addEventListener("load", () => setTimeout(trigger, 150));
    // Fallback in case the load event never fires in this host.
    setTimeout(trigger, 600);
  }
};
